import { Button } from 'components/atoms';
import { Form, Formik, FormikHelpers } from 'formik';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';

import { FormInput } from 'components/molecules';
import { PublicPage } from 'components/templates';
import { useMutateOptions } from 'hooks';
import i18next from 'i18next';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import { PiUser } from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLogin } from 'services/auth';
import * as Yup from 'yup';

type LoginFormModel = {
  email: string;
  password: string;
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email(i18next.t('formik-error.invalid-email'))
    .required(i18next.t('formik-error.required')),
  password: Yup.string()
    .min(3, i18next.t('formik-error.invalid-password'))
    .required(i18next.t('formik-error.required')),
});

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const login = useLogin();
  const mutateOptions = useMutateOptions<LoginFormModel>();

  const { ownerLogo } = useStoreState();

  const onLogin = async (
    values: LoginFormModel,
    helpers: FormikHelpers<LoginFormModel>
  ) => {
    await login.mutateAsync(values, mutateOptions(helpers));
  };

  const navigateToResetPasswordRequest = (email: string) =>
    navigate(`/reset-password/request?email=${email}`);

  const navigateToResendConfirmEmail = (email: string) =>
    navigate(`/confirm-email/resend?email=${email}`);

  return (
    <Formik
      validateOnBlur
      validationSchema={validationSchema}
      initialValues={{
        email: params.get('email') ?? '',
        password: '',
      }}
      onSubmit={onLogin}>
      {({ values }) => (
        <PublicPage
          header={
            <>
              <PiUser />
              {t('login.header')}
            </>
          }
          footer={
            <p
              className='cursor-pointer p-2 w-full text-center'
              onClick={() => navigateToResendConfirmEmail(values.email)}>
              {t('login.resend-confirm-email')}
            </p>
          }
          creative={
            <div
              className='flex flex-col items-center h-full w-96 text-sm text-center gap-5 p-8'
              style={{ backgroundColor: 'var(--secondary-soft)' }}>
              <img
                src={
                  ownerLogo
                    ? ownerLogo.pictureLink
                    : `${process.env.PUBLIC_URL}/icons/magellup_logo.png`
                }
                alt='magellan_logo'
                className='h-14 w-auto'
              />
              <p className='w-full'>
                Rozwiązaniem dla większych wydawców, które zapewni im
                profesjonalną obsługę oraz pozwoli zaoszczędzić czas i pieniądze
                jest MagelUP! Dzięki wykorzystaniu tego systemu będą mogli
                regularnie maksymalizować swoje zyski i minimalizować koszty
                operacyjne, ponieważ uzyskają pełną kontrolę nad swoim
                wydawnictwem
              </p>
              <p className='w-full'>
                MagelUP zapewnia wydawcom sprawdzone moduły dystrybucyjne
                obejmujące kanały sprzedaży cyfrowej i fizycznej oraz integrację
                z zewnętrznymi kanałami dystrybucji. Otrzymują również moduł do
                profesjonalnego zarządzania katalogiem produktów Artystów oraz
                rozbudowany i skupiony wyłącznie na nich system rozliczeniowy,
                który poradzi sobie nawet z najbardziej skomplikowanymi umowami
                i danymi!
              </p>
            </div>
          }>
          <Form>
            <FormInput.Text
              name='email'
              type='email'
              label={t('input.label.email')}
            />
            <div className='flex flex-col gap-1 w-full'>
              <FormInput.Text
                name='password'
                type='password'
                label={t('input.label.password')}
              />
              <p
                className='cursor-pointer w-full text-end text-sm p-2 pr-12 opacity-65'
                onClick={() => navigateToResetPasswordRequest(values.email)}>
                {t('login.forgot-password')}
              </p>
            </div>
            <Button
              type='submit'
              isLoading={login.isPending}
              variant={BUTTON_VARIANT.submit}
              fullWidth>
              <Button.Text text={t('login.button')} />
            </Button>
          </Form>
        </PublicPage>
      )}
    </Formik>
  );
};
export default Login;
